/**
 * ObjectLibraryPanel: lists every inventory object type from the catalog,
 * grouped by category. Tapping an item adds a new instance near the
 * canvas top-left, which the coordinator then drags into position.
 *
 * AUTO-NUMBERING REMOVED (Priority 3):
 * Objects added individually no longer get "Round Table 1", "Round Table 2"
 * labels. They get the plain display name. Coordinators who want numbers
 * can type them manually via the Inspector label field.
 *
 * The generated-layout engine (placement.ts) still uses sequential labels
 * for its generated tables — this change only affects manual placement.
 */

import { useEditorStore } from '../../store/useEditorStore';
import type { ObjectCatalogEntry, ObjectCategory } from '../../domain/types';

interface ObjectLibraryPanelProps {
  catalog: ObjectCatalogEntry[];
}

const CATEGORY_LABELS: Record<ObjectCategory, string> = {
  guest_seating: 'Seating',
  activity_zone: 'Dance Floor & DJ',
  feature_zone: 'Features',
  annotation: 'Notes',
};

const CATEGORY_ORDER: ObjectCategory[] = ['guest_seating', 'activity_zone', 'feature_zone', 'annotation'];

/** Center-to-center spacing for a row of ceremony chairs, in feet. */
const CHAIR_ROW_SPACING_FT = 1.8;

export function ObjectLibraryPanel({ catalog }: ObjectLibraryPanelProps) {
  const addObject = useEditorStore((state) => state.addObject);
  const selectObject = useEditorStore((state) => state.selectObject);
  const objects = useEditorStore((state) => state.objects);

  function handleAdd(entry: ObjectCatalogEntry) {
    const offset = objects.length % 8;
    const id = crypto.randomUUID();
    // No auto-numbering: use plain display name (or 'Note' for text notes
    // so coordinators know to type a message there).
    const label = entry.itemType === 'text_note' ? 'Note' : entry.displayName;
    addObject({
      id,
      type: entry.itemType,
      x: 4 + offset * 1.5,
      y: 4 + offset * 1.5,
      rotation: 0,
      label,
      seats: entry.defaultSeats,
      locked: false,
      zIndex: 5,
    });
    selectObject(id);
  }

  /**
   * Creates N real standalone_chair LayoutObjects in a horizontal row,
   * evenly spaced at CHAIR_ROW_SPACING_FT center-to-center.
   * Each chair is independent — it can be moved, deleted, or rotated
   * individually after placement. Chair count (seats=1 each) is added
   * to the total chair usage tracked by LiveCountersBar.
   */
  function handleAddChairRow(count: number) {
    const chairEntry = catalog.find((c) => c.itemType === 'standalone_chair');
    if (!chairEntry) return;

    const offset = objects.length % 8;
    const baseX = 4 + offset * 1.5;
    const baseY = 4 + offset * 1.5;
    // All chairs in this row share a rowGroupId so "Select Row" can find them
    const rowGroupId = `row-${crypto.randomUUID()}`;
    let firstId: string | null = null;

    for (let i = 0; i < count; i++) {
      const id = crypto.randomUUID();
      if (i === 0) firstId = id;
      addObject({
        id,
        type: 'standalone_chair',
        x: baseX + i * CHAIR_ROW_SPACING_FT,
        y: baseY,
        rotation: 0,
        label: 'Chair',
        seats: chairEntry.defaultSeats ?? 1,
        locked: false,
        zIndex: 4,
        rowGroupId,
      });
    }
    if (firstId) selectObject(firstId);
  }

  const hasChair = catalog.some((c) => c.itemType === 'standalone_chair');

  return (
    <div className="flex h-full flex-col overflow-y-auto p-3">
      <h3 className="px-1 font-display text-base text-ink-700">Add to layout</h3>

      {/* Chair row presets — only shown if standalone_chair is in the catalog */}
      {hasChair && (
        <div className="mt-4">
          <p className="px-1 font-body text-xs uppercase tracking-wide text-ink-400">Chair Rows</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              onClick={() => handleAddChairRow(6)}
              className="rounded-lg border border-ivory-300 bg-white px-2 py-3 text-center font-body text-xs text-ink-600 transition-colors hover:border-gold-300 hover:bg-gold-50"
            >
              Row of 6 Chairs
            </button>
            <button
              onClick={() => handleAddChairRow(8)}
              className="rounded-lg border border-ivory-300 bg-white px-2 py-3 text-center font-body text-xs text-ink-600 transition-colors hover:border-gold-300 hover:bg-gold-50"
            >
              Row of 8 Chairs
            </button>
          </div>
        </div>
      )}

      {CATEGORY_ORDER.map((category) => {
        const items = catalog.filter((c) => c.category === category);
        if (items.length === 0) return null;
        return (
          <div key={category} className="mt-4">
            <p className="px-1 font-body text-xs uppercase tracking-wide text-ink-400">
              {CATEGORY_LABELS[category]}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {items.map((entry) => (
                <button
                  key={entry.itemType}
                  onClick={() => handleAdd(entry)}
                  className="rounded-lg border border-ivory-300 bg-white px-2 py-3 text-center font-body text-xs text-ink-600 transition-colors hover:border-gold-300 hover:bg-gold-50"
                >
                  {entry.displayName}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
